import React, { useCallback, useEffect, useState } from 'react'

import { useStaticData } from '@mc/static-data-react'
import { usePromotion } from '@mc/promo-utils'
import {
  DISCOUNT_VIEWED,
  PROMO_EXPLAINER_MODAL_SHOULD_BE_SHOWN,
} from '@mc/constants'
import { useUserInteractions } from '@mc/visibility'

import { ExplainerModalContent } from './ExplainerModalContent'
import { DUMMY_PROMO_CODE } from '../../lib/constants'
import {
  trackNavigationClicked,
  trackExplainerModalViewed,
} from '../../lib/events'
import { isExplainerModalDismissed } from '../../lib/helpers'

export const ExplainerModal = ({ pageSource }: { pageSource: string }) => {
  const [show, setShow] = useState(false)

  const { query, promoCode } = useStaticData<{
    query?: { promo_id?: string }
    promoCode?: string
  }>()
  const promoId = query?.promo_id

  const { isPromotionOn } = usePromotion()

  const shouldShowModal = useCallback(() => {
    const discountSlug = promoCode || promoId
    const discountModalViewed = window.sessionStorage.getItem(DISCOUNT_VIEWED)
    const isPromoDiscountSlug = discountSlug === DUMMY_PROMO_CODE

    /* Full set of conditions for showing the modal are the same as bogo modal:
     * - User has not clicked anywhere in the page
     * - 6 seconds have passed since page load
     * - User has already scrolled
     * - User has not dismissed a discount modal recently or if they did,
     * they no longer have a promo code (in this case we are also adding that the promo code is not the seasonal promo code)
     * - The user has already dismissed the modal by clicking the close button
     */

    return (
      !isExplainerModalDismissed() &&
      (!!discountModalViewed || !discountSlug || isPromoDiscountSlug) &&
      isPromotionOn
    )
  }, [isPromotionOn, promoCode, promoId])

  const setActions = useCallback(() => {
    if (shouldShowModal()) {
      setShow(true)
      trackExplainerModalViewed({ pageSource })
    }
  }, [pageSource, shouldShowModal])

  useEffect(() => {
    /*
     * in order to avoid collision with other modals, it is set this key to
     * let know other modals about the chance of the modal being shown.
     */
    window.sessionStorage.setItem(
      PROMO_EXPLAINER_MODAL_SHOULD_BE_SHOWN,
      shouldShowModal()?.toString() ?? '',
    )
  }, [shouldShowModal])

  useUserInteractions({
    timedActions: setActions,
    enableConditionFn: isExplainerModalDismissed,
  })

  const closeModal = () => {
    setShow(false)
    trackNavigationClicked()
  }

  if (!isPromotionOn) return null

  if (!show) return <></>

  return (
    <ExplainerModalContent closeModal={closeModal} pageSource={pageSource} />
  )
}
